import { useEffect, useRef, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg, fmtDate } from "../lib/api";
import { useSite } from "../lib/store";
import { Card, Field, ImageInput, PageTitle, Toggle, useList } from "./ui";

const get = (o, path) => path.split(".").reduce((a, k) => (a == null ? a : a[k]), o);
const setIn = (o, path, v) => {
  const keys = path.split(".");
  const n = structuredClone(o);
  let cur = n;
  keys.slice(0, -1).forEach((k) => {
    cur[k] = cur[k] ?? {};
    cur = cur[k];
  });
  cur[keys.at(-1)] = v;
  return n;
};

const SECTIONS = {
  company: ["Firma a kontakty", [["company.name", "Název e-shopu"], ["company.owner", "Provozovatel (jméno / firma)"], ["company.street", "Ulice"], ["company.city", "Město"], ["company.zip", "PSČ"], ["company.ico", "IČO"], ["company.dic", "DIČ"], ["company.register_note", "Zápis v rejstříku"], ["company.email", "E-mail"], ["company.phone", "Telefon"], ["company.bank_account", "Číslo účtu"], ["company.iban", "IBAN (pro QR platbu)"], ["social.instagram", "Instagram URL"], ["social.facebook", "Facebook URL"]]],
  home: ["Texty hlavní stránky", [["texts.categories_title", "Nadpis kategorií"], ["texts.about_title", "Banner – nadpis"], ["texts.about_text", "Banner – text", true], ["texts.about_points", "Banner – odrážky (každá na nový řádek)", true, "lines"], ["texts.about_image", "Banner – obrázek", false, "image"], ["texts.featured_title", "Nadpis vybraných produktů"], ["texts.usp.0.title", "Výhoda 1 – nadpis"], ["texts.usp.0.text", "Výhoda 1 – text"], ["texts.usp.1.title", "Výhoda 2 – nadpis"], ["texts.usp.1.text", "Výhoda 2 – text"], ["texts.usp.2.title", "Výhoda 3 – nadpis"], ["texts.usp.2.text", "Výhoda 3 – text"], ["texts.newsletter_title", "Newsletter – nadpis"], ["texts.newsletter_text", "Newsletter – text"], ["texts.footer_slogan", "Patička – slogan"], ["texts.footer_note", "Patička – ručně psaný vzkaz"]]],
  pages: ["Stránky", [["texts.about_page", "O nás", true], ["texts.contact_page", "Kontakt – úvodní text", true], ["texts.payment_page", "Doprava a platba", true], ["texts.complaints_page", "Reklamace", true]]],
  legal: ["Obchodní podmínky a GDPR", [["legal.terms", "Obchodní podmínky (## = nadpis)", true], ["legal.gdpr", "GDPR a cookies (## = nadpis)", true]]],
  seo: ["SEO", [["seo.title", "Titulek webu"], ["seo.description", "Meta popis", true], ["seo.keywords", "Klíčová slova"]]],
  emails: ["Automatické e-maily", [["emails.welcome", "Uvítací e-mail po registraci", true], ["emails.order_received", "Potvrzení objednávky", true], ["emails.paid", "Poděkování po zaplacení (s fakturou)", true]]],
};

const useSettings = () => {
  const [s, setS] = useState(null);
  const { reload } = useSite();
  useEffect(() => {
    let live = true;
    adminApi.get("/settings").then((r) => live && setS((cur) => cur || r.data));
    return () => { live = false; };
  }, []);
  const save = async (body) => {
    try {
      const { data } = await adminApi.put("/settings", body || s);
      setS(data);
      reload();
      toast.success("Nastavení uloženo");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return [s, setS, save];
};

export function SiteSettings() {
  const [s, setS, save] = useSettings();
  const [tab, setTab] = useState("company");
  if (!s) return <div className="h-64 animate-pulse rounded-3xl bg-white" />;
  const [title, fields] = SECTIONS[tab];
  return (
    <div data-testid="admin-settings">
      <PageTitle title="Obsah webu" sub="Údaje provozovatele, texty, právní stránky, SEO a e-maily" action={<button className="btn-sage" onClick={() => save()} data-testid="settings-save-btn"><Save className="h-4 w-4" /> Uložit vše</button>} />
      <div className="no-scrollbar mb-5 flex gap-2 overflow-x-auto">
        {Object.entries(SECTIONS).map(([k, [l]]) => <button key={k} onClick={() => setTab(k)} className={`shrink-0 rounded-full px-4 py-2 text-sm ${tab === k ? "bg-[#7A8460] text-white" : "border bg-white"}`} data-testid={`settings-tab-${k}`}>{l}</button>)}
      </div>
      <Card title={title}>
        <div className="grid gap-4 md:grid-cols-2">
          {fields.map(([path, label, ta, kind]) => {
            const v = get(s, path);
            if (kind === "image") return <div key={path}><p className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#6E7565]">{label}</p><ImageInput value={v} onChange={(x) => setS(setIn(s, path, x))} testid={`settings-${path}`} /></div>;
            return (
              <Field key={path} label={label} textarea={ta} className={ta ? "md:col-span-2" : ""} rows={tab === "legal" ? 18 : 5}
                value={kind === "lines" ? (v || []).join("\n") : v || ""} testid={`settings-${path}`}
                onChange={(e) => setS(setIn(s, path, kind === "lines" ? e.target.value.split("\n") : e.target.value))} />
            );
          })}
        </div>
      </Card>
    </div>
  );
}

export function InvoiceDesigner() {
  const [s, setS, save] = useSettings();
  const [url, setUrl] = useState("");
  const timer = useRef();
  const inv = s?.invoice;
  useEffect(() => {
    if (!inv) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await adminApi.post("/invoice-preview", inv, { responseType: "blob" });
        setUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(r.data); });
      } catch (e) {
        toast.error("Náhled se nepodařilo vytvořit");
      }
    }, 600);
  }, [inv]);
  if (!s) return <div className="h-64 animate-pulse rounded-3xl bg-white" />;
  const set = (k, v) => setS({ ...s, invoice: { ...inv, [k]: v } });
  const color = (k, l) => (
    <label className="flex items-center justify-between gap-3 text-sm"><span>{l}</span>
      <input type="color" value={inv[k]} onChange={(e) => set(k, e.target.value)} className="h-10 w-16 cursor-pointer rounded-xl border" data-testid={`invoice-${k}`} /></label>
  );
  return (
    <div data-testid="admin-invoice">
      <PageTitle title="Návrh faktury" sub="Rozložení, barvy, písmo a texty – náhled se mění živě" action={<button className="btn-sage" onClick={() => save({ invoice: inv })} data-testid="invoice-save-btn"><Save className="h-4 w-4" /> Uložit fakturu</button>} />
      <div className="grid gap-6 xl:grid-cols-[380px_1fr]">
        <div className="space-y-4">
          <Card title="Rozložení">
            <div className="grid grid-cols-3 gap-2">
              {[["classic", "Klasické"], ["modern", "Moderní pruh"], ["sidebar", "Boční lišta"]].map(([v, l]) => (
                <button key={v} onClick={() => set("layout", v)} className={`rounded-2xl border-2 p-3 text-xs font-semibold ${inv.layout === v ? "border-[#7A8460] bg-[#E9EFE0]" : ""}`} data-testid={`invoice-layout-${v}`}>{l}</button>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {[["Sans", "Bezpatkové"], ["Serif", "Patkové"]].map(([v, l]) => <button key={v} onClick={() => set("font", v)} className={`rounded-2xl border-2 p-3 text-sm ${inv.font === v ? "border-[#7A8460] bg-[#E9EFE0]" : ""} ${v === "Serif" ? "font-serif" : ""}`} data-testid={`invoice-font-${v}`}>{l}</button>)}
            </div>
            <label className="mt-4 block text-sm">Velikost písma: <b>{inv.font_size}</b>
              <input type="range" min="8" max="13" step="0.5" value={inv.font_size} onChange={(e) => set("font_size", Number(e.target.value))} className="w-full accent-[#7A8460]" data-testid="invoice-font-size" /></label>
          </Card>
          <Card title="Barvy"><div className="space-y-3">{color("primary_color", "Hlavní barva")}{color("accent_color", "Doplňková barva")}{color("text_color", "Barva textu")}</div></Card>
          <Card title="Texty">
            <div className="space-y-3">
              <Field label="Nadpis dokladu" value={inv.title} onChange={(e) => set("title", e.target.value)} testid="invoice-title" />
              <Field label="Text pod logem" value={inv.header_note} onChange={(e) => set("header_note", e.target.value)} testid="invoice-header-note" />
              <Field label="Patička (max 3 řádky)" textarea value={inv.footer_text} onChange={(e) => set("footer_text", e.target.value)} testid="invoice-footer" />
              <Field label="Podpis" value={inv.signature} onChange={(e) => set("signature", e.target.value)} testid="invoice-signature" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Prefix čísla" value={inv.prefix} onChange={(e) => set("prefix", e.target.value)} testid="invoice-prefix" />
                <Field label="Splatnost (dny)" type="number" value={inv.due_days} onChange={(e) => set("due_days", Number(e.target.value))} testid="invoice-due" />
              </div>
              <Field label="Začít číslovat od (pořadové číslo další faktury)" type="number" min="1" value={inv.next_number || ""} placeholder="např. 25" onChange={(e) => set("next_number", e.target.value ? Number(e.target.value) : "")} testid="invoice-next-number" />
              <p className="text-xs text-[#6E7565]">Další faktura bude mít číslo <b>{inv.prefix}{new Date().getFullYear()}{String(inv.next_number || 1).padStart(4, "0")}</b> (pokud už nebyla vystavena vyšší).</p>
              <Toggle label="Zobrazit logo kopretiny" checked={inv.show_logo} onChange={(v) => set("show_logo", v)} testid="invoice-show-logo" />
              <Toggle label="QR platba na nezaplacených dokladech" checked={inv.show_qr} onChange={(v) => set("show_qr", v)} testid="invoice-show-qr" />
            </div>
          </Card>
        </div>
        <Card title="Živý náhled">
          {url ? <iframe title="Náhled faktury" src={url} className="h-[80vh] w-full rounded-2xl border" data-testid="invoice-preview-frame" /> : <div className="h-[60vh] animate-pulse rounded-2xl bg-[#F7F3EA]" />}
        </Card>
      </div>
    </div>
  );
}

export function Integrations() {
  const [s, setS, save] = useSettings();
  const [testing, setTesting] = useState("");
  if (!s) return <div className="h-64 animate-pulse rounded-3xl bg-white" />;
  const integ = s.integrations || {};
  const cg = integ.comgate || { test: true };
  const em = integ.email || { provider: "resend" };
  const setCg = (k, v) => setS({ ...s, integrations: { ...integ, comgate: { ...cg, [k]: v } } });
  const st = integ.stripe || {};
  const bk = integ.bank || {};
  const setSt = (k, v) => setS({ ...s, integrations: { ...integ, stripe: { ...st, [k]: v } } });
  const setBk = (k, v) => setS({ ...s, integrations: { ...integ, bank: { ...bk, [k]: v } } });
  const setEm = (k, v) => setS({ ...s, integrations: { ...integ, email: { ...em, [k]: v } } });
  const origin = window.location.origin;
  const test = async (provider) => {
    setTesting(provider);
    try {
      await save({ integrations: s.integrations || {} });
      const { data } = await adminApi.post("/test-email", { provider });
      if (data.sent) toast.success(`Testovací e-mail odeslán přes ${({ gmail: "Gmail", resend_own: "vlastní Resend" })[data.provider] || "spravovaný Resend"} na ${data.to}`);
      else toast.error(`Odeslání selhalo: ${data.error || ""}`);
      if (data.own_error) toast.warning(`Vlastní Resend: ${data.own_error}`);
      if (data.gmail_error) toast.warning(`Gmail: ${data.gmail_error} – použit záložní Resend`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setTesting("");
  };
  const Url = ({ label, value }) => (
    <div className="rounded-2xl bg-[#F7F3EA] p-3 text-xs"><p className="font-semibold">{label}</p><code className="break-all">{value}</code></div>
  );
  return (
    <div data-testid="admin-integrations">
      <PageTitle title="Platby a e-maily" sub="Napojení platebních bran a odesílání systémových e-mailů" action={<button className="btn-sage" onClick={() => save({ integrations: s.integrations || {} })} data-testid="integrations-save-btn"><Save className="h-4 w-4" /> Uložit</button>} />
      <div className="grid gap-6 xl:grid-cols-2">
        <Card title="Stripe – platba kartou">
          <div className="space-y-4">
            <Toggle label="Zapnout platbu kartou (Stripe) v pokladně" checked={!!st.enabled} onChange={(v) => setSt("enabled", v)} testid="stripe-enabled" />
            <Field label="Secret key (sk_live_… / sk_test_…)" type="password" value={st.secret_key || ""} onChange={(e) => setSt("secret_key", e.target.value)} testid="stripe-secret" />
            <Field label="Webhook signing secret (whsec_…)" type="password" value={st.webhook_secret || ""} onChange={(e) => setSt("webhook_secret", e.target.value)} testid="stripe-webhook" />
            <Url label="Webhook URL (Stripe → Developers → Webhooks, událost checkout.session.completed)" value={`${origin}/api/stripe/webhook`} />
            <p className="text-xs text-[#6E7565]">Když je vypnuto, platba kartou se zákazníkům vůbec nenabízí.</p>
          </div>
        </Card>
        <Card title="Bankovní převod / QR platba">
          <div className="space-y-4">
            <Toggle label="Zapnout platbu převodem / QR v pokladně" checked={!!bk.enabled} onChange={(v) => setBk("enabled", v)} testid="bank-enabled" />
            <p className="text-sm text-[#6E7565]">Číslo účtu a IBAN vyplňte v Nastavení → Firma a kontakty. Bez vyplněného IBAN se platba nenabídne ani po zapnutí.</p>
            <p className="text-xs text-[#6E7565]">Objednávky převodem potvrzujete ručně v sekci Objednávky.</p>
          </div>
        </Card>
        <Card title="Comgate – platební brána">
          <div className="space-y-4">
            <Toggle label="Zapnout platbu Comgate v pokladně" checked={!!cg.enabled} onChange={(v) => setCg("enabled", v)} testid="comgate-enabled" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="ID obchodu (merchant)" value={cg.merchant || ""} onChange={(e) => setCg("merchant", e.target.value)} testid="comgate-merchant" />
              <Field label="Heslo (secret)" type="password" value={cg.secret || ""} onChange={(e) => setCg("secret", e.target.value)} testid="comgate-secret" />
            </div>
            <Toggle label="Testovací režim (bez skutečných plateb)" checked={cg.test !== false} onChange={(v) => setCg("test", v)} testid="comgate-test" />
            <p className="text-sm text-[#6E7565]">V klientském portálu Comgate (Integrace → Nastavení obchodu) vyplňte tyto adresy a povolte vaši IP / způsob propojení „HTTP POST“:</p>
            <Url label="URL pro předání výsledku platby (push / callback)" value={`${origin}/api/comgate/callback`} />
            <Url label="Návratová URL – zaplaceno / zrušeno / čeká" value={`${origin}/platba/comgate?order=\${refId}`} />
            <p className="text-xs text-[#6E7565]">Po zaplacení se automaticky vystaví faktura, odešle e-mail a odemknou soubory – stejně jako u Stripe.</p>
          </div>
        </Card>
        <Card title="Odesílání e-mailů">
          <div className="space-y-4">
            <div className="grid gap-2 sm:grid-cols-3">
              {[["resend", "Resend (spravovaný)", "Funguje hned, bez nastavení"], ["resend_own", "Vlastní Resend", "Váš účet a doména – nejspolehlivější"], ["gmail", "Gmail", "Odesílá z vaší Gmail adresy"]].map(([v, l, d]) => (
                <button key={v} onClick={() => setEm("provider", v)} className={`rounded-2xl border-2 p-3 text-left ${em.provider === v ? "border-[#7A8460] bg-[#E9EFE0]" : ""}`} data-testid={`email-provider-${v}`}>
                  <p className="text-sm font-semibold">{l}</p><p className="text-xs text-[#6E7565]">{d}</p>
                </button>
              ))}
            </div>
            <div className="space-y-3 rounded-2xl bg-[#F7F3EA] p-4">
              <p className="text-sm font-semibold">Vlastní Resend účet</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Resend API klíč (re_…)" type="password" value={em.resend_api_key || ""} onChange={(e) => setEm("resend_api_key", e.target.value)} testid="resend-own-key" />
                <Field label="Odesílací adresa (ověřená doména)" value={em.resend_from || ""} onChange={(e) => setEm("resend_from", e.target.value)} placeholder="info@hravamaterinka.cz" testid="resend-own-from" />
              </div>
              <p className="text-xs text-[#6E7565]">1) Založte účet na resend.com → 2) Domains → Add domain (hravamaterinka.cz) a u registrátora domény přidejte DNS záznamy, které Resend ukáže (SPF, DKIM) → 3) API Keys → Create API key (Sending access) → 4) vložte klíč a adresu sem, vyberte „Vlastní Resend“, uložte a otestujte. Když vlastní Resend selže, použije se spravovaný.</p>
              <button className="btn-ghost" disabled={!!testing} onClick={() => test("resend_own")} data-testid="test-resend-own-btn">{testing === "resend_own" ? "Odesílám…" : "Test vlastního Resend"}</button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Gmail adresa" value={em.gmail_user || ""} onChange={(e) => setEm("gmail_user", e.target.value)} placeholder="vas.ucet@gmail.com" testid="gmail-user" />
              <Field label="Heslo aplikace (16 znaků)" type="password" value={em.gmail_password || ""} onChange={(e) => setEm("gmail_password", e.target.value)} testid="gmail-password" />
            </div>
            <p className="text-xs text-[#6E7565]">Heslo aplikace vytvoříte v účtu Google → Zabezpečení → Dvoufázové ověření → Hesla aplikací. Když Gmail selže, e-mail se automaticky odešle přes Resend.</p>
            <div className="flex flex-wrap gap-2">
              <button className="btn-ghost" disabled={!!testing} onClick={() => test("resend")} data-testid="test-resend-btn">{testing === "resend" ? "Odesílám…" : "Test Resend"}</button>
              <button className="btn-ghost" disabled={!!testing} onClick={() => test("gmail")} data-testid="test-gmail-btn">{testing === "gmail" ? "Odesílám…" : "Test Gmail"}</button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export function Communication() {
  const [emails] = useList("/emails");
  const [news] = useList("/newsletter");
  const [msgs] = useList("/messages");
  return (
    <div data-testid="admin-communication">
      <PageTitle title="E-maily a zprávy" sub="Log automatických e-mailů, zprávy z kontaktního formuláře a odběratelé" />
      <div className="grid gap-6 xl:grid-cols-2">
        <Card title="Odeslané e-maily">
          <div className="max-h-[60vh] divide-y overflow-y-auto">
            {(emails || []).map((e) => (
              <div key={e.id} className="py-2 text-sm" data-testid="email-log-row">
                <div className="flex justify-between gap-2"><b className="truncate">{e.subject}</b><span className={`rounded-full px-2 text-xs ${e.status === "sent" ? "bg-[#E9EFE0]" : "bg-[#F8E1D7]"}`}>{e.status === "sent" ? "odesláno" : "chyba"}</span></div>
                <p className="text-xs text-[#6E7565]">{e.to} · {fmtDate(e.created_at)} · {({ welcome: "Registrace", order_received: "Objednávka", paid: "Faktura + poděkování", owner: "Upozornění pro vás", test: "Test", voucher: "Dárkový poukaz", newsletter: "Newsletter", newsletter_test: "Newsletter – test" })[e.kind] || e.kind} · {e.provider || "resend"}{e.error ? ` · ${e.error}` : ""}</p>
              </div>
            ))}
            {emails && !emails.length && <p className="text-sm text-[#6E7565]">Zatím žádné e-maily.</p>}
          </div>
        </Card>
        <div className="space-y-6">
          <Card title="Zprávy z webu">
            <div className="max-h-[40vh] space-y-3 overflow-y-auto">
              {(msgs || []).map((m) => <div key={m.id} className="rounded-2xl bg-[#F7F3EA] p-3 text-sm"><b>{m.name}</b> · <a href={`mailto:${m.email}`} className="text-[#7A8460]">{m.email}</a><p className="mt-1 whitespace-pre-line">{m.message}</p></div>)}
              {msgs && !msgs.length && <p className="text-sm text-[#6E7565]">Žádné zprávy.</p>}
            </div>
          </Card>
          <Card title={`Odběratelé novinek (${news?.length || 0})`}>
            <div className="max-h-[30vh] overflow-y-auto text-sm">{(news || []).map((n) => <p key={n.email} className="border-b py-1.5">{n.email}</p>)}</div>
          </Card>
        </div>
      </div>
    </div>
  );
}
